/**
 * Base Mapper - Abstract class for implementing the Mapper Pattern
 * Provides common functionality for mapping between DTOs and UI models
 */

import { pascalToHuman } from '../utils/stringUtils';

export abstract class BaseMapper<TDto, TUiModel> {
  /**
   * Map a single DTO to UI model
   */
  abstract mapToUiModel(dto: TDto): TUiModel;

  /**
   * Map a single UI model to DTO (for create/update operations)
   */
  abstract mapToDto(uiModel: Partial<TUiModel>): Partial<TDto>;

  /**
   * Map an array of DTOs to UI models
   */
  mapToUiModels(dtos: TDto[]): TUiModel[] {
    return dtos.map(dto => this.mapToUiModel(dto));
  }

  /**
   * Map an array of UI models to DTOs
   */
  mapToDtos(uiModels: Partial<TUiModel>[]): Partial<TDto>[] {
    return uiModels.map(model => this.mapToDto(model));
  }

  /**
   * Safe date mapping helper
   */
  protected mapDate(dateInput: string | Date | null | undefined): Date | null {
    if (!dateInput) return null;

    const parsedDate = dateInput instanceof Date ? new Date(dateInput.getTime()) : new Date(dateInput);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * Safe string mapping helper
   */
  protected mapString(value: string | null | undefined): string | undefined {
    return value || undefined;
  }

  /**
   * Safe boolean mapping helper
   */
  protected mapBoolean(value: boolean | null | undefined, defaultValue = false): boolean {
    return value ?? defaultValue;
  }

  /**
   * Safe number mapping helper
   */
  protected mapNumber(value: number | null | undefined): number | undefined {
    return value ?? undefined;
  }

  /**
   * Safe array mapping helper
   */
  protected mapArray<T>(value: T[] | null | undefined): T[] {
    return value ?? [];
  }

  /**
   * Map enum values safely
   */
  protected mapEnum<T>(value: string | number | null | undefined, enumMap: Record<string | number, T>, defaultValue: T): T {
    if (value === null || value === undefined) return defaultValue;
    return enumMap[value] ?? defaultValue;
  }

  // ===== String-enum <-> label helpers (strongly-typed) =====
  /**
   * Convert a string-literal enum value to a human-friendly label using Pascal/camel case splitting.
   * Example: 'ProjectOwner' -> 'Project Owner'
   */
  protected toEnumLabel<TEnum extends string>(value: TEnum | null | undefined): string | undefined {
    if (!value) return undefined;
    return pascalToHuman(value);
  }

  /**
   * Convert a human-friendly label (or raw value) back to a string-literal enum value
   * by matching against the allowed values. Matching is case/space insensitive.
   * If label is already an allowed value, it is returned as-is.
   */
  protected labelToEnum<TEnum extends string>(
    labelOrValue: string | null | undefined,
    allowed: readonly TEnum[],
    defaultValue?: TEnum
  ): TEnum | undefined {
    if (!labelOrValue) return defaultValue;

    // If already an allowed value, return directly
    if (allowed.includes(labelOrValue as TEnum)) return labelOrValue as TEnum;

    const normalize = (s: string): string => s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const normalizedInput = normalize(labelOrValue);

    // Try matching by normalizing: remove spaces/symbols and compare case-insensitive
    for (const v of allowed) {
      if (normalize(v) === normalizedInput) return v;
      // Also try converting the enum to human form and compare
      const human = pascalToHuman(v);
      if (normalize(human) === normalizedInput) return v;
    }
    return defaultValue;
  }
}

/**
 * Interface for mappers that support bulk operations
 */
export interface IBulkMapper<TDto, TUiModel> {
  mapBulkResult(result: { successes: TDto[]; errors: any[] }): { successes: TUiModel[]; errors: any[] };
}

/**
 * Interface for mappers that support pagination
 */
export interface IPaginationMapper {
  mapPaginationInfo(paginationData: any): {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

/**
 * Utility type for extracting DTO type from mapper
 */
export type MapperDtoType<T> = T extends BaseMapper<infer TDto, any> ? TDto : never;

/**
 * Utility type for extracting UI model type from mapper
 */
export type MapperUiModelType<T> = T extends BaseMapper<any, infer TUiModel> ? TUiModel : never;
